import React from 'react';
import { useNavPages } from '../lib/useNavPages';
import { Link } from 'react-router-dom';
import { Mail, Send, MessageCircle, Phone } from 'lucide-react';
import { useStoreSettings } from '../lib/useStoreSettings';

const ContactItem = ({ href, icon: Icon, label, testid, external }) => (
  <a href={href} data-testid={testid} {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
    className="group inline-flex items-center gap-2.5 text-[13.5px] text-neutral-300 hover:text-white transition-colors">
    <span className="grid place-items-center w-8 h-8 rounded-full border border-white/10 bg-white/5 group-hover:bg-brand group-hover:border-brand transition-colors"><Icon size={14} /></span>
    {label}
  </a>
);

const Footer = () => {
  const store = useStoreSettings();
  const pages = useNavPages();
  const wa = (store?.whatsapp || '').replace(/[^0-9]/g, '');
  const tel = (store?.phone || '').replace(/[^0-9+]/g, '');

  return (
    <footer data-testid="site-footer" className="relative bg-[#0c1210] text-neutral-400 overflow-hidden">
      <div aria-hidden className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[hsl(var(--brand))] to-transparent opacity-70" />
      <div aria-hidden className="pointer-events-none absolute -top-40 -left-24 w-[520px] h-[520px] rounded-full bg-[hsl(var(--brand)/0.10)] blur-3xl" />
      <div className="relative max-w-7xl mx-auto px-6 pt-16 pb-10 grid gap-12 md:grid-cols-12">
        <div className="md:col-span-5">
          <Link to="/" data-testid="footer-logo" className="inline-block leading-none">
            <div className="font-serif tracking-[0.35em] text-[24px] text-white">HEALSGLOBE</div>
            <div className="tracking-[0.4em] text-[10px] mt-1 text-brand font-semibold">EXPORTS</div>
          </Link>
          {store?.tagline && <p data-testid="footer-tagline" className="mt-6 max-w-sm text-[14px] leading-relaxed text-neutral-400">{store.tagline}</p>}
        </div>

        {pages.length > 0 && (
          <nav className="md:col-span-3" data-testid="footer-nav">
            <h3 className="text-[11px] uppercase tracking-[0.25em] text-neutral-500 mb-5">Information</h3>
            <ul className="space-y-3">
              {pages.map(p => (
                <li key={p.id}><Link to={p.to} data-testid={`footer-page-${p.slug}`} className="text-[14px] text-neutral-300 hover:text-white hover:translate-x-0.5 inline-block transition-[color,transform]">{p.title}</Link></li>
              ))}
            </ul>
          </nav>
        )}

        {(store?.support_email || store?.telegram || wa || tel) && (
        <div className="md:col-span-4" data-testid="footer-contact">
          <h3 className="text-[11px] uppercase tracking-[0.25em] text-neutral-500 mb-5">Contact</h3>
          <div className="flex flex-col gap-3">
            {store?.support_email && <ContactItem href={`mailto:${store.support_email}`} icon={Mail} label={store.support_email} testid="footer-email" />}
            {store?.telegram && <ContactItem href={`https://t.me/${store.telegram}`} icon={Send} label={`@${store.telegram}`} testid="footer-telegram" external />}
            {wa && <ContactItem href={`https://wa.me/${wa}`} icon={MessageCircle} label={store.whatsapp} testid="footer-whatsapp" external />}
            {tel && <ContactItem href={`tel:${tel}`} icon={Phone} label={store.phone} testid="footer-phone" />}
          </div>
        </div>
        )}
      </div>
      <div className="relative border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 pt-5 pb-24 sm:pb-5 flex flex-wrap items-center justify-between gap-3 text-[12px] text-neutral-500">
          <span data-testid="footer-copyright">© {new Date().getFullYear()} Healsglobe Exports. All rights reserved.</span>
          <span>Discreet worldwide shipping</span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
